import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const useStatusMessage = () =>
  useQuery({ queryKey: ["status-message"], queryFn: async () => (await api.get("/public/status")).data, staleTime: 60_000 });

export const PRESETS = [
  ["Online", ""],
  ["Bank holiday", "Closed for the bank holiday · back tomorrow"],
  ["Back Monday", "Closed for the weekend · replies from Monday"],
  ["4-hour replies", "Replies within 4 hours"],
  ["Reduced hours", "Reduced hours this week · replies may take longer"],
];

export const StatusMessage = ({ admin }) => {
  const { data } = useStatusMessage();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const custom = data?.text;

  const save = async (value = text) => {
    try {
      const { data: saved } = await api.put("/settings/status", { text: value });
      qc.setQueryData(["status-message"], (old) => ({ ...(old || {}), text: saved.text }));
      setEditing(false);
      toast.success(saved.text ? "Status message updated" : "Back to “Support online”");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); save(); }} className="ml-auto flex flex-col items-end gap-1.5" data-testid="status-edit-form">
        <div className="flex items-center gap-1.5">
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} maxLength={120} placeholder="e.g. Replies within 4 hours · closed Mon (bank holiday)" className="field !h-8 w-72 !py-1 text-xs" data-testid="status-edit-input" />
          <button type="submit" className="rounded-md p-1 text-emerald-400 hover:bg-slate-800" title="Save" data-testid="status-edit-save"><Check className="h-4 w-4" /></button>
          <button type="button" onClick={() => setEditing(false)} className="rounded-md p-1 text-slate-400 hover:bg-slate-800" title="Cancel" data-testid="status-edit-cancel"><X className="h-4 w-4" /></button>
        </div>
        <div className="flex flex-wrap justify-end gap-1" data-testid="status-presets">
          {PRESETS.map(([label, value]) => (
            <button key={label} type="button" onClick={() => save(value)} data-testid={`status-preset-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors ${value === (custom || "") ? "border-rose-500/60 bg-rose-500/10 text-rose-200" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white"}`}>
              {label}
            </button>
          ))}
        </div>
      </form>
    );
  }
  return (
    <div className="ml-auto hidden items-center gap-2 font-mono text-[11px] uppercase tracking-widest sm:flex" data-testid="support-status">
      <span className={`h-1.5 w-1.5 rounded-full ${custom ? "bg-amber-400" : "bg-emerald-400"}`} />
      <span className={`max-w-[28rem] truncate ${custom ? "text-amber-200" : "text-slate-500"}`} data-testid="support-status-text">{custom || "Support online"}</span>
      {admin && (
        <button onClick={() => { setText(custom || ""); setEditing(true); }} className="rounded-md p-1 text-slate-500 hover:bg-slate-800 hover:text-white" title="Edit status message" data-testid="status-edit-button">
          <Pencil className="h-3 w-3" />
        </button>
      )}
    </div>
  );
};
